import React, { useState } from 'react';
import { MdCheck, MdEdit } from 'react-icons/md';
import styled from 'styled-components';
import { GoChevronLeft, GoChevronRight } from 'react-icons/go';
import { breakpoints, globalBreakpointsW } from '@/components/responsive';

interface QuizOptionV2 {
  title: string;
  subtitle?: string;
  prompt: string;
}

interface QuizQuestionV2 {
  question: string;
  options: QuizOptionV2[];
  allowMultiple?: boolean;
}

interface QuizDataV2 {
  quiz: QuizQuestionV2[];
}

type QuestionAnswer = {
  selectedIndices: number[];
  freeTextSelected: boolean;
  freeText: string;
};

type AnswersV2 = Record<number, QuestionAnswer>;

const extractRawString = (content: unknown): string => {
  if (typeof content === `string`) return content;
  if (Array.isArray(content)) return content.map(extractRawString).join(``);
  return String(content ?? ``);
};

const parseQuizV2Data = (content: unknown): QuizDataV2 | null => {
  try {
    return JSON.parse(extractRawString(content));
  } catch {
    return null;
  }
};

const getAnswerPromptText = (
  question: QuizQuestionV2,
  answer: QuestionAnswer,
): string => {
  const parts = answer.selectedIndices.map(
    (i) => question.options[i]?.prompt ?? question.options[i]?.title ?? ``,
  );
  if (answer.freeTextSelected && answer.freeText) parts.push(answer.freeText);
  return parts.join(`\n`);
};

const buildSubmitMessage = (
  quiz: QuizQuestionV2[],
  answers: AnswersV2,
): string =>
  quiz
    .map((q, i) => {
      const answer = answers[i];
      if (!answer) return `**${q.question}**\nSkipped`;
      return `**${q.question}**\n${getAnswerPromptText(q, answer)}`;
    })
    .join(`\n\n`);

const dispatchAnswerToChat = (message: string) => {
  window.dispatchEvent(new CustomEvent(`ai-chat-input`, { detail: message }));
};

const isQuestionAnswered = (answer: QuestionAnswer | undefined): boolean => {
  if (!answer) return false;
  if (answer.selectedIndices.length > 0) return true;
  return answer.freeTextSelected && answer.freeText.trim().length > 0;
};

interface QuizNavigationProps {
  currentStep: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

function QuizNavigation({
  currentStep,
  total,
  onPrev,
  onNext,
}: QuizNavigationProps) {
  return (
    <NavigationRow>
      <NavChevronButton onClick={onPrev} disabled={currentStep === 0}>
        <GoChevronLeft />
      </NavChevronButton>
      <NavStepLabel>{`${currentStep + 1} of ${total}`}</NavStepLabel>
      <NavChevronButton onClick={onNext} disabled={currentStep >= total - 1}>
        <GoChevronRight />
      </NavChevronButton>
    </NavigationRow>
  );
}

interface OptionBadgeProps {
  index: number;
  selected: boolean;
  allowMultiple: boolean;
}

function OptionBadge({ index, selected, allowMultiple }: OptionBadgeProps) {
  return (
    <Badge $selected={selected} $allowMultiple={allowMultiple}>
      {selected ? (
        <MdCheck color="#0068db" size={18} />
      ) : (
        !allowMultiple && <span>{index + 1}</span>
      )}
    </Badge>
  );
}

interface RegularOptionProps {
  option: QuizOptionV2;
  index: number;
  selected: boolean;
  allowMultiple: boolean;
  onSelect: () => void;
}

function RegularOption({
  option,
  index,
  selected,
  allowMultiple,
  onSelect,
}: RegularOptionProps) {
  return (
    <OptionCard $selected={selected} onClick={onSelect}>
      <OptionBadge
        index={index}
        selected={selected}
        allowMultiple={allowMultiple}
      />
      <OptionTextBlock>
        <OptionTitle>{option.title}</OptionTitle>
        {option.subtitle && <OptionSubtitle>{option.subtitle}</OptionSubtitle>}
      </OptionTextBlock>
    </OptionCard>
  );
}

interface FreeTextOptionProps {
  index: number;
  selected: boolean;
  allowMultiple: boolean;
  value: string;
  onActivate: () => void;
  onEnsureSelected: () => void;
  onTextChange: (v: string) => void;
}

function FreeTextOption({
  index,
  selected,
  allowMultiple,
  value,
  onActivate,
  onEnsureSelected,
  onTextChange,
}: FreeTextOptionProps) {
  return (
    <OtherOptionCard $selected={selected} onClick={onActivate}>
      <OtherTextBlock>
        <OptionBadge
          index={index}
          selected={selected}
          allowMultiple={allowMultiple}
        />
        <OptionTitle>Other</OptionTitle>
      </OtherTextBlock>

      <OptionTextBlock>
        <FreeTextRow
          className="ai-quiz-free-text"
          onClick={(e) => {
            e.stopPropagation();
            onEnsureSelected();
          }}
        >
          <MdEdit size={15} color="#aaa" />
          <FreeTextInput
            value={value}
            placeholder="Something else"
            onChange={(e) => {
              onTextChange(e.target.value);
            }}
            onClick={(e) => {
              e.stopPropagation();
              onEnsureSelected();
            }}
          />
        </FreeTextRow>
      </OptionTextBlock>
    </OtherOptionCard>
  );
}

interface QuizRendererV2Props {
  children: unknown;
  isLatestMessage?: boolean;
}

export function QuizRendererV2({
  children,
  isLatestMessage = true,
}: QuizRendererV2Props) {
  const data = parseQuizV2Data(children);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<AnswersV2>({});

  if (!data?.quiz?.length) return null;

  const { quiz } = data;
  const isLastStep = currentStep === quiz.length - 1;
  const currentAnswer = answers[currentStep];
  const activeQuestion = quiz[currentStep];
  const allowMultiple = activeQuestion.allowMultiple ?? false;

  const writeAnswer = (patch: QuestionAnswer) =>
    setAnswers((prev) => ({ ...prev, [currentStep]: patch }));

  const toggleRegularOption = (index: number) => {
    if (!allowMultiple) {
      const alreadySelected = currentAnswer?.selectedIndices?.includes(index);
      writeAnswer({
        selectedIndices: alreadySelected ? [] : [index],
        freeTextSelected: false,
        freeText: currentAnswer?.freeText ?? ``,
      });
      return;
    }
    const prev = currentAnswer?.selectedIndices ?? [];
    const selectedIndices = prev.includes(index)
      ? prev.filter((i) => i !== index)
      : [...prev, index];
    writeAnswer({
      selectedIndices,
      freeTextSelected: currentAnswer?.freeTextSelected ?? false,
      freeText: currentAnswer?.freeText ?? ``,
    });
  };

  const activateFreeText = () => {
    if (currentAnswer?.freeTextSelected) {
      writeAnswer({
        selectedIndices: currentAnswer?.selectedIndices ?? [],
        freeTextSelected: false,
        freeText: currentAnswer?.freeText ?? ``,
      });
      return;
    }

    if (!allowMultiple) {
      writeAnswer({
        selectedIndices: [],
        freeTextSelected: true,
        freeText: currentAnswer?.freeText ?? ``,
      });
      return;
    }

    writeAnswer({
      selectedIndices: currentAnswer?.selectedIndices ?? [],
      freeTextSelected: true,
      freeText: currentAnswer?.freeText ?? ``,
    });
  };

  const ensureFreeTextSelected = () => {
    if (currentAnswer?.freeTextSelected) return;

    writeAnswer({
      selectedIndices: allowMultiple
        ? currentAnswer?.selectedIndices ?? []
        : [],
      freeTextSelected: true,
      freeText: currentAnswer?.freeText ?? ``,
    });
  };

  const selectAndUpdateFreeText = (value: string) =>
    writeAnswer({
      selectedIndices: allowMultiple
        ? currentAnswer?.selectedIndices ?? []
        : [],
      freeTextSelected: true,
      freeText: value,
    });

  const goToPrev = () => setCurrentStep((p) => Math.max(p - 1, 0));

  const goToNext = () =>
    setCurrentStep((p) => Math.min(p + 1, quiz.length - 1));

  const advanceOrSubmit = () => {
    if (isLastStep) {
      dispatchAnswerToChat(buildSubmitMessage(quiz, answers));
    } else {
      goToNext();
    }
  };

  if (!isLatestMessage) {
    return null;
  }

  return (
    <QuizV2Container className="ai-chat-quiz-v2 quiz-v2-container">
      <style>
        {`
          .quiz-v2-container {
            container-type: inline-size;
            container-name: quiz-v2;
          }

          @container quiz-v2 (max-width: ${globalBreakpointsW.md}px) {
            .quiz-v2-content-wrapper {
              width: 100%;
              padding: 2rem 2rem;
            }

            .ai-quiz-free-text {
              max-width: 100%;
            }
          }
        `}
      </style>
      <Wrapper className="quiz-v2-content-wrapper">
        {quiz.length > 1 && (
          <QuizNavigation
            currentStep={currentStep}
            total={quiz.length}
            onPrev={goToPrev}
            onNext={goToNext}
          />
        )}

        <QuestionSection>
          <QuestionText>{activeQuestion.question}</QuestionText>
          <OptionsStack>
            {activeQuestion.options.map((option, i) => (
              <RegularOption
                key={i}
                option={option}
                index={i}
                allowMultiple={allowMultiple}
                selected={currentAnswer?.selectedIndices?.includes(i) ?? false}
                onSelect={() => toggleRegularOption(i)}
              />
            ))}
            <FreeTextOption
              index={activeQuestion.options.length}
              allowMultiple={allowMultiple}
              selected={currentAnswer?.freeTextSelected ?? false}
              value={currentAnswer?.freeText ?? ``}
              onActivate={activateFreeText}
              onEnsureSelected={ensureFreeTextSelected}
              onTextChange={selectAndUpdateFreeText}
            />
          </OptionsStack>
        </QuestionSection>

        <FooterRow className="quiz-v2-footer">
          {allowMultiple && (
            <SelectedCount>
              {`Selected: ${
                (currentAnswer?.selectedIndices?.length ?? 0) +
                (currentAnswer?.freeTextSelected ? 1 : 0)
              }`}
            </SelectedCount>
          )}
          <SkipButton onClick={advanceOrSubmit}>Skip</SkipButton>
          <SubmitButton
            onClick={advanceOrSubmit}
            disabled={!isQuestionAnswered(currentAnswer)}
          >
            {isLastStep ? `Submit` : `Next`}
          </SubmitButton>
        </FooterRow>
      </Wrapper>
    </QuizV2Container>
  );
}

const QuizV2Container = styled.div`
  text-align: start;
  border-radius: 2rem;
  margin: 1rem auto;
  font-family: Eesti, sans-serif;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.04);
  border: solid 0.5px rgba(151, 151, 151, 0.17);
  background-blend-mode: lighten;
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.7),
    rgba(255, 255, 255, 0.7)
  );

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Wrapper = styled.div`
  padding: 3rem 0rem;
  display: flex;
  flex-direction: column;
  width: 83%;
  padding-right: 0.8rem;
`;

const NavigationRow = styled.div`
  display: flex;
  align-items: center;

  gap: 0.75rem;
  margin-bottom: 2.5rem;
  flex-direction: row;
`;

const NavChevronButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  color: #555;
  font-size: 1.4rem;
  line-height: 1;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: #888;
    color: #111;
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const NavStepLabel = styled.span`
  font-size: 1.4rem;
  color: #333;
  font-weight: 500;
`;

const QuestionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const QuestionText = styled.p`
  font-size: 2rem;
  font-weight: 500;
  color: #111;
  margin: 0;
  line-height: 1.4;
  white-space: pre-wrap;

  ${breakpoints.down(`md`)} {
    font-size: 1.8rem;
  }
`;

const OptionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const OptionCard = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.2rem 1.5rem;
  border-radius: 0.75rem;
  background: ${({ $selected }) =>
    $selected ? `rgb(133 206 255 / 0.2)` : `rgb(216 216 216 / 0.2)`};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ $selected }) => ($selected ? `#deeeff` : `#efefef`)};
  }
`;

const OtherOptionCard = styled(OptionCard)`
  && {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }
`;

const Badge = styled.div<{ $selected: boolean; $allowMultiple: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
  font-size: 1.4rem;
  font-weight: 500;
  color: ${({ $selected }) => ($selected ? `#0068db` : `#666`)};
  background: ${({ $selected, $allowMultiple }) => {
    if ($selected) return `rgb(136 205 254 / 0.4)`;
    return !$allowMultiple ? `rgb(216 216 216 / 0.3)` : `white`;
  }};
  border: ${({ $selected, $allowMultiple }) =>
    !$selected && $allowMultiple ? `1.5px solid #e0e0e0` : `none`};
  transition: background 0.15s, color 0.15s, border-color 0.15s;
`;

const OptionTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
  padding-top: 0.5rem;
  width: 100%;
`;

const OtherTextBlock = styled(OptionTextBlock)`
  && {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }
`;

const OptionTitle = styled.span`
  font-size: 1.5rem;
  font-weight: 500;
  color: #111;
  line-height: 1.4;
  white-space: pre-wrap;
`;

const OptionSubtitle = styled.span`
  font-size: 1.3rem;
  color: #777;
  line-height: 1.4;
  white-space: pre-wrap;
`;

const FreeTextRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.6rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  padding: 0.6rem 0.8rem;
  max-width: 55%;
`;

const FreeTextInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 1.3rem;
  color: #333;
  font-family: Eesti, sans-serif;
  flex: 1;
  min-width: 0;

  &::placeholder {
    color: #bbb;
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 3rem;
`;

const SelectedCount = styled.span`
  font-size: 1.6rem;
  color: #aaa;
  margin-right: auto;
`;

const SkipButton = styled.button`
  padding: 1rem 2rem;
  border-radius: 3rem;
  border: 1.5px solid #d4d4d4;
  background: transparent;
  color: #666;
  font-size: 1.4rem;
  font-family: Eesti, sans-serif;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;

  &:hover {
    border-color: #888;
    color: #333;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2.5rem;
  border-radius: 3rem;
  background: #111;
  color: #fff;
  font-size: 1.4rem;
  font-family: Eesti, sans-serif;
  font-weight: 500;
  cursor: pointer;
  border: 1.5px solid #111;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.4;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
